import React, { useState, useEffect, useSyncExternalStore } from 'react';
import { useAppSelector } from '@/store/hooks';
import { useI18nText } from '@/lib/utils/i18nText';
import { getActiveHeavyRefreshCount, subscribeHeavyRefresh } from '@/lib/utils/indexedDbCache';

const BARS = 12;

/** Subscribes to heavy-cache refresh count for UI annotation. */
function useHeavyRefreshActive(): boolean {
  const count = useSyncExternalStore(
    subscribeHeavyRefresh,
    getActiveHeavyRefreshCount,
    getActiveHeavyRefreshCount,
  );
  return count > 0;
}

function SpinnerDots({ elapsed, message }: { elapsed: number; message?: React.ReactNode }) {
  return (
    <div className="page-spinner-block">
      <div className="page-spinner">
        {Array.from({ length: BARS }, (_, i) => (
          <div
            key={i}
            className="page-spinner-dot"
            style={{
              transform: `rotate(${i * 30}deg)`,
              animationDelay: `${-((BARS - i) / BARS) * 1.2}s`,
            }}
          />
        ))}
        <span className="page-spinner-elapsed">{elapsed}s</span>
      </div>
      {message && <div className="page-spinner-message">{message}</div>}
    </div>
  );
}

/** Localized two-line message shown while a heavy IndexedDB cache is being refreshed. */
function useHeavyRefreshMessage(active: boolean): React.ReactNode {
  const { t } = useI18nText();
  if (!active) return undefined;
  return (
    <>
      <div>
        {t('Refreshing system cache data...', '시스템 캐시 데이터를 갱신중입니다.')}
      </div>
      <div className="page-spinner-message__sub">
        {t(
          'Loading may take a few seconds while fresh data is fetched.',
          '데이터를 새로 받아오는 동안 잠시 로딩이 지연될 수 있습니다.',
        )}
      </div>
      <div>
        <i className="fa fa-database text-[40px] text-ggc-text-gray-light opacity-30 mt-6" aria-hidden="true"></i>
      </div>
    </>
  );
}

/** 전역 스피너 오버레이 — count > 0일 때 표시 */
export default function Spinner() {
  const { active } = useAppSelector((s) => s.spinner);
  const heavyRefreshing = useHeavyRefreshActive();
  const heavyMessage = useHeavyRefreshMessage(heavyRefreshing);
  const [elapsed, setElapsed] = useState(0);

  useEffect(() => {
    if (!active) {
      setElapsed(0);
      return;
    }
    const start = Date.now();
    const id = setInterval(() => {
      setElapsed(Math.floor((Date.now() - start) / 1000));
    }, 1000);
    return () => clearInterval(id);
  }, [active]);

  if (!active) return null;

  return (
    <div className="page-spinner-overlay">
      <SpinnerDots elapsed={elapsed} message={heavyMessage} />
    </div>
  );
}

/** 페이지 초기 로딩 시 사용하는 공유 컴포넌트 */
export function PageLoader() {
  const heavyRefreshing = useHeavyRefreshActive();
  const heavyMessage = useHeavyRefreshMessage(heavyRefreshing);
  const [elapsed, setElapsed] = useState(0);

  useEffect(() => {
    const start = Date.now();
    const id = setInterval(() => {
      setElapsed(Math.floor((Date.now() - start) / 1000));
    }, 1000);
    return () => clearInterval(id);
  }, []);

  return (
    <div className="page-spinner-wrapper">
      <SpinnerDots elapsed={elapsed} message={heavyMessage} />
    </div>
  );
}

/** 테이블 셀 등에서 사용하는 소형 인라인 스피너 */
export function InlineSpinner({ size = 16 }: { size?: number }) {
  return (
    <span
      className="inline-spinner"
      style={{ width: size, height: size }}
    />
  );
}
